import { useMemo, useCallback, useState } from "react";
import { useLLMModels } from "@/api/hooks";
import { useDebounce } from "@/hooks/use-debounce";

interface UseLLMDropdownOptions {
  enabled?: boolean;
  /** Boş string ("") verilirse provider_type filtresi uygulanmaz, tüm modeller listelenir. */
  providerType?: string;
}

export const useLLMDropdown = (options: UseLLMDropdownOptions = {}) => {
  const { enabled = true, providerType = "llm" } = options;

  // Local state for dropdown
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleItems, setVisibleItems] = useState(20);

  // 🚀 Debounce search query - 500ms bekle (API çağrılarını optimize et)
  const debouncedSearchQuery = useDebounce(searchQuery || "", 500);

  // Backend'den LLM models getir - API search ile
  const {
    data: modelsData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLLMModels({
    enabled,
    provider_type: providerType,
    searchTerm: debouncedSearchQuery, // 🚀 Debounced API search
  });

  // Tüm modeller (API'den geliyor)
  const allModels = useMemo(() => {
    const models = modelsData?.pages
      ?.flatMap((page) => page.data.result)
      ?.filter((model) => !model.isDeleted);
    return models || [];
  }, [modelsData]);

  // Görünür modeller
  const visibleModels = useMemo(() => {
    return allModels.slice(0, visibleItems);
  }, [allModels, visibleItems]);

  // Scroll handler
  const handleScroll = useCallback(
    async (element: HTMLDivElement | null) => {
      if (!element) return;

      const { scrollTop, scrollHeight, clientHeight } = element;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        // Client-side görünürlük artışı
        setVisibleItems((prev) => prev + 20);
        // Server-side sonraki sayfayı getir
        if (hasNextPage && !isFetchingNextPage) {
          await fetchNextPage();
        }
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  // Search query update
  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query);
    setVisibleItems(20); // Reset visible items when search changes
  }, []);

  // Reset visible items
  const resetVisibleItems = useCallback(() => {
    setVisibleItems(20);
  }, []);

  return {
    // State
    searchQuery,
    visibleModels,
    allModels,
    isLoading,
    error,

    // Actions
    updateSearchQuery,
    handleScroll,
    resetVisibleItems,

    // Infinite state
    hasNextPage,
    isLoadingMore: isFetchingNextPage,

    // Computed
    hasMoreItems: visibleItems < allModels.length,
    totalModels: allModels.length,
    filteredCount: allModels.length,
  };
};
